import { CognitoIdentityCredentials } from "aws-sdk";
import awsAppConfig from './config';
import lsUtil from "../../common/lsUtil";

let refreshIntervalId = null;
const REFRESH_INTERVAL_MS = 720000; // 12 minutes

export function tryCookieAuth(cookieName, onSuccess, onError) {
    /**
     * tryCookieAuth attempts to authenticate the user using a cookie.
     * It retrieves the cookie value, sets up AWS credentials using CognitoIdentityCredentials
     * (GetCredentialsForIdentity), which binds the session to a specific Cognito identity.
     * This is required for IoT Core to apply identity-attached policies.
     *
     * @param {string} cookiename - The name of the cookie to retrieve.
     * @param {function} onSuccess - Callback function to call on successful authentication.
     * @param {function} onError - Callback function to call on error.
     *
     * returns {boolean} - Returns true if the cookie was found, false
     * otherwise. When the caller gets false back, it will fall back to the
     * normal auth flow.
     *
     * It is called by CognitoUtil#refreshLogin. onSuccess is implemented in
     * various places, often as a functiont that accepts no args, but the "main"
     * case is onRefreshLoginSuccess in SessionManager.js.  It expects an array
     * of key-value pairs, containing any user attributes that we would get from
     * the user pool. We need to make sure that function gets the attributes it
     * needs to funciton normally.
     */
    if (!cookieName) {
        return false;
    }

    const cookieValue = getCookieValue(cookieName);
    if (!cookieValue) {
        return false;
    }

    // Read the Cognito identity ID cookie (set by the login lambda)
    const identityId = getCookieValue('raven.identityid');
    if (!identityId) {
        console.warn('prototypeAuth: raven.identityid cookie not found — MQTT will not initialize');
    }

    console.log('found token in cookie: ', cookieName);
    window.AWS.config.credentials = new CognitoIdentityCredentials({
        IdentityPoolId: awsAppConfig.IdentityPoolId,
        IdentityId: identityId || undefined,
        Logins: {
            'cognito-identity.amazonaws.com': cookieValue
        }
    });

    lsUtil.setItem(lsUtil.AWS_COGNITO_USER_POOL_ID_TOKEN_JWT, cookieValue);

    window.AWS.config.credentials.refresh((err) => {
        if (err) {
            onError(err);
            return;
        }
        console.log('AWS credentials refreshed successfully via CognitoIdentityCredentials');

        // attributeList is an array of key-value pairs that we would normally get from the user pool.
        const attributeList = [
            {
                Name: 'email',
                Value: 'travis.gray@ravenconnected.com',
                getName: function() {
                    return this.Name;
                },
                getValue: function() {
                    return this.Value;
                }
            }
        ];
        
        const defaultStage = process.env.REACT_APP_DEFAULT_STAGE;
        if (defaultStage) {
            attributeList.push({
                Name: 'custom:linked_raven_stage',
                Value: defaultStage,
                getName: function() {
                    return this.Name;
                },
                getValue: function() {
                    return this.Value;
                }
            });
        }
        onSuccess(attributeList, { identityId });
        startTokenRefreshLoop();
    });

    return true;
}

export function startTokenRefreshLoop() {
    if (refreshIntervalId) {
        clearInterval(refreshIntervalId);
    }
    console.log('Starting token refresh loop (every 12 minutes)');
    refreshIntervalId = setInterval(refreshToken, REFRESH_INTERVAL_MS);
}

export function stopTokenRefreshLoop() {
    if (refreshIntervalId) {
        clearInterval(refreshIntervalId);
        refreshIntervalId = null;
        console.log('Token refresh loop stopped');
    }
}

export async function refreshToken() {
    try {
        const response = await fetch('/prod/login/refresh', {
            method: 'GET',
            credentials: 'same-origin'
        });

        if (response.ok) {
            const data = await response.json();
            console.log('Token refresh response received, expiresAt:', data.expiresAt);

            const newToken = getCookieValue('raven.idtoken');
            if (newToken) {
                const identityId = getCookieValue('raven.identityid');

                window.AWS.config.credentials = new CognitoIdentityCredentials({
                    IdentityPoolId: awsAppConfig.IdentityPoolId,
                    IdentityId: identityId || undefined,
                    Logins: {
                        'cognito-identity.amazonaws.com': newToken
                    }
                });

                window.AWS.config.credentials.refresh((err) => {
                    if (err) {
                        console.error('Error refreshing AWS credentials after token refresh:', err);
                        return;
                    }
                    console.log('AWS credentials refreshed successfully after token refresh');
                });

                lsUtil.setItem(lsUtil.AWS_COGNITO_USER_POOL_ID_TOKEN_JWT, newToken);
                console.log('Token refresh successful');
            } else {
                console.warn('Token refresh: new token not found in cookie after refresh');
            }
        } else if (response.status === 401) {
            stopTokenRefreshLoop();
            console.warn('Session is no longer valid (401). Token refresh loop stopped. User will need to re-authenticate.');
        } else {
            console.error('Token refresh failed with status:', response.status);
        }
    } catch (error) {
        console.error('Error during token refresh:', error);
    }
}

function getCookieValue(cookiename) {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === cookiename) {
            return value;
        }
    }
    return null;
}
